import { useState } from 'react';
import { X, Calendar } from 'lucide-react';
import { useCreateVotingSessionMutation } from '@/store/api/votingApi';
import { set } from 'react-hook-form';
import moment from 'moment';
import UploadImage from '@/components/common/UploadImage';

interface CreateSessionDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function CreateSessionDialog({ open, setOpen }: CreateSessionDialogProps) {
  const [createVotingSession, { isLoading }] = useCreateVotingSessionMutation();
  const [rounds, setRounds] = useState(1);
  const [imageLoading, setImageLoading] = useState(false);

  const [errors, setErrors] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    image: '',
  });
  const [form, setForm] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    image: '',
  });

  const handleCreateSession = async () => {
    let newErrors = {
      title: '',
      description: '',
      startTime: '',
      endTime: '',
      image: '',
    };

    if (!form.image) {
      newErrors.image = 'Banner image is required';
    }

    if (!form.title.trim()) {
      newErrors.title = 'Session title is required';
    }

    if (!form.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!form.startTime) {
      newErrors.startTime = 'Start date is required';
    }

    if (!form.endTime) {
      newErrors.endTime = 'End date is required';
    }

    setErrors(newErrors);

    if (
      newErrors.title ||
      newErrors.description ||
      newErrors.startTime ||
      newErrors.endTime ||
      newErrors.image
    ) {
      return;
    }

    try {
      await createVotingSession({
        title: form.title,
        description: form.description,
        image: form.image,
        startTime: moment(form.startTime)
          .subtract(5, 'hours')
          .subtract(30, 'minutes')
          .format('YYYY-MM-DDTHH:mm'),
        endTime: moment(form.endTime)
          .subtract(5, 'hours')
          .subtract(30, 'minutes')
          .format('YYYY-MM-DDTHH:mm'),
        round: rounds,
      }).unwrap();
      setOpen(false);
      setForm({
        title: '',
        description: '',
        startTime: '',
        endTime: '',
        image: '',
      });
    } catch (err) {
      console.error('Failed to create voting session', err);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div
        className="w-full max-w-[720px] bg-[#0b1220] border border-gray-800 rounded-xl shadow-xl 
      max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 sm:p-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-semibold text-white">
                Create Voting Session
              </h2>

              <p className="text-gray-400 text-sm mt-1">
                Create a new voting session with multiple rounds.
              </p>
            </div>

            <button onClick={() => setOpen(false)} className="p-1 hover:bg-gray-800 rounded">
              <X className="text-gray-400 hover:text-white" />
            </button>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <UploadImage
                label="Banner"
                onChange={(url) => {
                  setForm((prev) => ({
                    ...prev,
                    image: url,
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    image: '',
                  }));
                }}
                setIsImageUploading={setImageLoading}
              />
              {errors.image && <p className="text-red-500 text-xs">{errors.image}</p>}
            </div>
            <div>
              <label className="text-sm text-gray-300">Session Title *</label>

              <input
                value={form.title}
                onChange={(e) => {
                  setForm((f) => ({ ...f, title: e.target.value }));

                  if (errors.title) {
                    setErrors((prev) => ({ ...prev, title: '' }));
                  }
                }}
                className="w-full mt-2 bg-[#050b18] border border-gray-700 rounded-lg px-4 py-2 text-white outline-none focus:border-blue-500"
                placeholder="Enter session title"
              />
              {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
            </div>

            <div>
              <label className="text-sm text-gray-300">Description</label>

              <textarea
                value={form.description}
                onChange={(e) => {
                  setForm((f) => ({ ...f, description: e.target.value }));

                  if (errors.description) {
                    setErrors((prev) => ({ ...prev, description: '' }));
                  }
                }}
                rows={3}
                className="w-full mt-2 bg-[#050b18] border border-gray-700 rounded-lg px-4 py-2 text-white outline-none focus:border-blue-500 resize-none"
                placeholder="Describe the purpose of this voting session."
              />
              {errors.description && <p className="text-red-500 text-xs">{errors.description}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-300">start Date</label>

                <div className="flex items-center gap-2 mt-2 border border-gray-700 bg-[#050b18] rounded-lg px-3 py-2">
                  <Calendar size={16} className="text-gray-500" />

                  <input
                    value={form.startTime}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, startTime: e.target.value }));

                      if (errors.startTime) {
                        setErrors((prev) => ({ ...prev, startTime: '' }));
                      }
                    }}
                    type="datetime-local"
                    className="bg-transparent outline-none text-white w-full"
                  />
                </div>
                {errors.startTime && (
                  <p className="text-red-500 mt-1 text-xs">{errors.startTime}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-300">End Date</label>

                <div className="flex items-center gap-2 mt-2 border border-gray-700 bg-[#050b18] rounded-lg px-3 py-2">
                  <Calendar size={16} className="text-gray-500" />

                  <input
                    value={form.endTime}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, endTime: e.target.value }));

                      if (errors.endTime) {
                        setErrors((prev) => ({ ...prev, endTime: '' }));
                      }
                    }}
                    type="datetime-local"
                    className="bg-transparent outline-none text-white w-full"
                  />
                </div>
                {errors.endTime && <p className="text-red-500 mt-1 text-xs">{errors.endTime}</p>}
              </div>

              <div>
                <label className="text-sm text-gray-300">Rounds of Voting *</label>

                <div className="flex items-center mt-2 border border-gray-700 rounded-lg overflow-hidden">
                  <button
                    disabled={rounds == 1}
                    className="px-3 py-2 bg-[#050b18] hover:bg-[#0f172a]"
                    onClick={() => setRounds((r) => Math.max(1, r - 1))}
                  >
                    -
                  </button>

                  <input
                    value={rounds}
                    readOnly
                    className="w-full text-center bg-[#0b1220] text-white focus:outline-none"
                  />

                  <button
                    className="px-3 py-2 bg-[#050b18] hover:bg-[#0f172a]"
                    onClick={() => setRounds((r) => Math.min(10, r + 1))}
                  >
                    +
                  </button>
                </div>

                <p className="text-xs text-gray-500 mt-1">Number of rounds for voting (1-10)</p>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              By creating a voting session, participants will be able to vote across multiple
              rounds. You can configure session details and manage rounds after creation.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
            <button
              onClick={() => setOpen(false)}
              className="w-full sm:w-auto px-4 py-2 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800"
            >
              Cancel
            </button>

            <button
              onClick={handleCreateSession}
              className="w-full sm:w-auto px-5 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-white"
            >
              {isLoading ? 'Creating...' : 'Create Session'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
